import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ConnectionState, Track } from 'livekit-client';
import {
  TrackReference,
  useConnectionState,
  useRoomContext,
  useTracks,
  VideoTrack,
} from '@livekit/components-react';
import styles from './AvatarVideoFeed.module.scss';

export function AvatarVideoFeed() {
  const { t } = useTranslation();
  const connectionState = useConnectionState();
  const room = useRoomContext();

  const trackRefs = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: false }  // Get all tracks, not just subscribed
  );

  // Find avatar track - looking for remote participant with camera
  const agentTrackRef = trackRefs.find(
    trackRef => !trackRef.participant.isLocal
  );

  const isConnecting =
    connectionState === ConnectionState.Connecting ||
    connectionState === ConnectionState.Reconnecting;

  // Debug logging
  useEffect(() => {
    console.log('[AvatarVideoFeed] Connection state:', connectionState);
    console.log('[AvatarVideoFeed] Room participants:', {
      local: room.localParticipant.identity,
      remote: Array.from(room.remoteParticipants.values()).map(p => ({
        identity: p.identity,
        name: p.name,
        tracks: Array.from(p.trackPublications.values()).map(pub => ({
          source: pub.source,
          kind: pub.kind,
          subscribed: pub.isSubscribed,
        })),
      })),
    });
    console.log('[AvatarVideoFeed] Available track refs:', trackRefs.map(ref => ({
      participant: ref.participant.identity,
      isLocal: ref.participant.isLocal,
      source: ref.source,
    })));
    console.log('[AvatarVideoFeed] Selected agent track:', agentTrackRef ? {
      participant: agentTrackRef.participant.identity,
      source: agentTrackRef.source,
    } : 'none');
  }, [connectionState, room, trackRefs, agentTrackRef]);

  if (isConnecting) {
    return (
      <div className={styles.avatarVideoFeed}>
        <div className={styles.connectingOverlay}>
          <div className={styles.connectingSpinner} />
          <p>
            {connectionState === ConnectionState.Reconnecting
              ? t('aiInterview.reconnecting')
              : t('aiInterview.connectingToInterviewer')}
          </p>
        </div>
      </div>
    );
  }

  if (!agentTrackRef) {
    return (
      <div className={styles.avatarVideoFeed}>
        <div className={styles.waitingOverlay}>
          <p>{t('aiInterview.tailoringExperience')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.avatarVideoFeed}>
      <VideoTrack
        trackRef={agentTrackRef as TrackReference}
        className={styles.avatarVideo}
      />
    </div>
  );
}
